import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Card, CardHeader, CardContent, Grid, makeStyles, TextField, FormGroup, FormControlLabel, Checkbox, Button, ExpansionPanel, ExpansionPanelSummary, ExpansionPanelDetails, ButtonGroup } from '@material-ui/core'
import EditableHeadingV2 from '../editable-heading/EditableHeadingV2'
import DropdownSelect from '../dropdown-select/DropdownSelect'
import { MandatoryType, QuestionType, InputType, FormValueType } from '../../state/constants/FormConstants'
import ScrollableTabs from '../tabs/Tabs'
import SimpleList from '../simple-list/SimpleList'
import Textbox from '../textbox/Textbox'
import SimpleTable from '../table/Table'
import { UUID } from '../../helpers/uuid'
import { useEffect } from 'react'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useTranslation } from 'react-i18next'

const Option = {
    id: null, // ?
    Name: null,
    SecondaryName: null,
    Type: null,
    Value: null,
    Color: null
}

const useStyles = makeStyles((theme) => ({
    // Both
    root: {
        width: "100%"
    },
    cardHeader: {
        padding: "8px",
        paddingBottom: "0px"
    },
    cardContent: {
        padding: "8px",
        '&:last-child': {
            paddingBottom: "8px",
        }
    },
    expansionDetails: {
        padding: "8px"
    },
    deleteButton: {
        float: "right",
        marginTop: "8px",
        marginBottom: "8px"
    },
    // Question
    questionRoot: {
        // backgroundColor: "rgba(0, 0, 0, 0.05)",
        // height: "378px" // TODO: Update with change to scrollable Tabs
    },
    multilineTextField: {
        // height: "186px"
    },
    checkbox: {
        margin: "7px"
    },
    button: {
        marginTop: "8px",
        marginBottom: "8px"
    },
    buttonGroup: {
        marginTop: "8px",
        marginBottom: "8px"
    }
}))

function FormQuestionCard(props) {

    const classes = useStyles(props)
    const [t, i18n] = useTranslation()

    const { question } = props
    const baseWidth = 3
    const setQuestion = props.setQuestion
    const [selection, setSelection] = useState([])
    const [shouldRender, setShouldRender] = useState(false)

    useEffect(() => {
        if (shouldRender) {
            setShouldRender(false)
        }

        return () => {
            setShouldRender(false)
        }
    }, [shouldRender])

    function configRender(type) {
        const types = QuestionType()
        switch (type) {
            case types.CHOICE.key:
            case types.TEXT.key:
                return true
            default:
                return false;
        }

    }

    const tabMap = {
        Question: {
            id: t('form.question'),
            render: renderQuestionTab,
            shouldRender: true
        },
        Config: {
            id: t('form.config'),
            render: renderConfigTab,
            shouldRender: configRender(question.Type) // Change to != null once Config implemented for all questions
        },
        Advanced: {
            id: t('common.advanced'),
            render: renderAdditionalTab,
            shouldRender: true
        },
    }

    function handleQuestionUpdate(field, value) {
        const updatedQuestion = Object.assign({}, question)
        // Split by '/'
        const fieldNames = field.split('/')

        var questionRef = updatedQuestion
        fieldNames.forEach((fieldName, index) => {
            if (questionRef[fieldName] == null) {
                questionRef[fieldName] = {}
            }

            if (index == fieldNames.length - 1) {
                questionRef[fieldName] = value
            } else {
                questionRef = questionRef[fieldName]
            }
        })

        setQuestion(updatedQuestion)
    }

    function renderComponent(item) {
        switch (item.type) {
            case "dropdown":
                return (
                    <DropdownSelect
                        value={item.value}
                        label={item.label}
                        handleChange={item.handleChange}
                        data={item.data}
                        disabled={props.disabled}
                    />
                )
            case "textField":
                return (
                    <TextField
                        value={item.value}
                        label={item.label}
                        onChange={item.handleChange}
                        margin="dense"
                        variant="outlined"
                        fullWidth
                        disabled={props.disabled}
                    />
                )
            case "textArray":
                return (
                    <Textbox
                        label={item.label}
                        value={item.value}
                        handleChange={item.handleChange}
                        disabled={props.disabled}
                    />
                )
            case "multilineTextField":
                return (
                    <TextField
                        value={item.value}
                        label={item.label}
                        onChange={item.handleChange}
                        margin="dense"
                        variant="outlined"
                        fullWidth
                        multiline
                        className={classes.multilineTextField}
                        rows="4"
                        disabled={props.disabled}
                    />
                )
            case "listView":
                return (
                    <SimpleTable
                        isEditing={!props.disabled}
                        isOrdered
                        isSticky
                        handleSelection={item.handleSelection}
                        isSelectable={true}
                        dataId={item.dataId}
                        headers={item.headers}
                        cells={item.createCells(item.headers)}
                        rows={item.value}
                        selected={item.data}
                        hidePagination
                    />
                )
            case "checkbox":
                return (
                    <FormGroup row className={classes.checkbox}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={item.value}
                                    onChange={item.handleChange}
                                    name={item.label}
                                    disabled={props.disabled}
                                />
                            }
                            label={item.label}
                        />
                    </FormGroup>
                )

            case "button":
                return (
                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={item.handleChange}
                        color={item.color}
                        disabled={item.disabled}
                    >
                        {item.label}
                    </Button>
                )
            case "buttonGroup":
                return (
                    <ButtonGroup fullWidth className={classes.buttonGroup}>
                        {item.data.map((button) => (
                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={button.handleChange}
                                color={button.color}
                                disabled={button.disabled}
                            >
                                {button.label}
                            </Button>
                        ))}
                    </ButtonGroup>
                )
            default:
                return null;
        }
    }

    function renderQuestionTab() {
        const questionGridData = [
            {
                type: "dropdown",
                label: t('common.type'),
                fieldName: "Type",
                data: Object.values(QuestionType()),
                value: question.Type,
                handleChange: (value) => handleQuestionUpdate("Type", value),
                width: 6
            },
            {
                type: "dropdown",
                label: t('common.mandatory'),
                fieldName: "IsMandatory",
                data: Object.values(MandatoryType()),
                value: question.IsMandatory,
                handleChange: (value) => handleQuestionUpdate("IsMandatory", value),
                width: 6
            },
            {
                type: "multilineTextField",
                label: t('common.description'),
                fieldName: "Description",
                data: null,
                value: question.Description,
                handleChange: (event) => handleQuestionUpdate("Description", event.target.value),
                width: 12
            },
        ]

        return (
            <Box>
                <Grid container spacing={2}>
                    {questionGridData.map(element => {
                        return (
                            <Grid item xs={element.width}>
                                {renderComponent(element)}
                            </Grid>
                        )
                    })}
                </Grid>
            </Box>
        )
    }

    function renderAdditionalTab() {
        const additionalGridData = [

            // {
            //     type: "textField",
            //     label: "Mask",
            //     fieldName: "Mask",
            //     data: null,
            //     value: question.Mask,
            //     handleChange: (event) => handleQuestionUpdate("Mask", event.target.value),
            //     width: 6
            // },
            {
                type: "textField",
                label: t('common.hint'),
                fieldName: "Hint",
                data: null,
                value: question.Hint,
                handleChange: (event) => handleQuestionUpdate("Hint", event.target.value),
                width: 12
            },
            {
                type: "textArray",
                label: t('common.tags'),
                fieldName: "Tags",
                data: null,
                value: (question.Tags != null ? question.Tags : []).map(item => ({ title: item, inputValue: item })),
                handleChange: (value) => handleQuestionUpdate("Tags", value.map(item => !!item?.inputValue ? item.inputValue : item)),
                width: 12
            },
            // {
            //     type: "textArray",
            //     label: "Validation",
            //     fieldName: "Validation",
            //     data: null,
            //     value: (question.Validation != null ? question.Validation : []).map(item => ({ title: item, inputValue: item })),
            //     handleChange: (value) => handleQuestionUpdate("Validation", value.map(item => item.inputValue)),
            //     width: 10
            // },
            {
                type: "checkbox",
                label: t('common.sticky'),
                fieldName: "IsSticky",
                data: null,
                value: question.IsSticky,
                handleChange: (event) => handleQuestionUpdate("IsSticky", event.target.checked),
                width: 2
            },
        ]
        return (
            <Box>
                <Grid container spacing={2}>
                    {additionalGridData.map(element => {
                        return (
                            <Grid item xs={element.width}>
                                {renderComponent(element)}
                            </Grid>
                        )
                    })}
                </Grid>
            </Box>
        )
    }

    function renderConfigTab(configType) {
        const types = QuestionType()
        const configGridData = {
            [types.CHOICE.key]: [
                {
                    type: "textField",
                    label: t('form.minimum-choices'),
                    fieldName: "MinChoices",
                    data: null,
                    value: question.MinChoices,
                    handleChange: (event) => handleQuestionUpdate("MinChoices", event.target.value),
                    width: 6
                },
                {
                    type: "textField",
                    label: t('form.maximum-choices'),
                    fieldName: "MaxChoices",
                    data: null,
                    value: question.MaxChoices,
                    handleChange: (event) => handleQuestionUpdate("MaxChoices", event.target.value),
                    width: 6
                },
                {
                    type: "textField",
                    label: t('form.entity-name'),
                    fieldName: "EntityName",
                    data: null,
                    value: question.EntityName,
                    handleChange: (event) => handleQuestionUpdate("ConfigurationOptions/EntityName", event.target.value),
                    width: 5
                },
                {
                    type: "checkbox",
                    label: t('form.fullscreen'),
                    fieldName: "Fullscreen",
                    data: null,
                    value: question.Fullscreen,
                    handleChange: (event) => handleQuestionUpdate("ConfigurationOptions/Fullscreen", event.target.checked),
                    width: 2
                },

                {
                    width: 9
                },
                {
                    type: "buttonGroup",
                    data: [
                        {
                            type: "button",
                            label: t('common.delete'),
                            disabled: selection.length === 0,
                            color: "primary",
                            handleChange: () => {
                                var updatedOptions = Object.assign([], question.Options);
                                if (updatedOptions == null) {
                                    //error
                                }
                                setSelection([])
                                const newData = updatedOptions.filter(item => !selection.includes(item.id));

                                handleQuestionUpdate("Options", newData)
                            }
                        },
                        {
                            type: "button",
                            label: t('common.add'),
                            disabed: false,
                            color: "secondary",
                            handleChange: () => {
                                var updatedOptions = Object.assign([], question.Options);
                                if (updatedOptions == null) {
                                    //error
                                }

                                const newOption = Object.assign({}, Option)
                                newOption.id = UUID()
                                const newData = [...updatedOptions]
                                newData.push(newOption)

                                handleQuestionUpdate("Options", newData)
                            }
                        }
                    ],
                    width: 3
                },
                {
                    type: "listView",
                    label: t('form.choices'),
                    fieldName: "Choices",
                    data: selection,
                    value: createChoiceData(question.Options),
                    dataId: "id",
                    headers: [
                        {
                            id: "name",
                            clickable: false,
                            align: "left",
                            label: t('common.name'),
                            type: "textField",
                            change: { identifier: "Name" }
                        },
                        {
                            id: "type",
                            clickable: false,
                            align: "left",
                            type: "dropDown",
                            typeData: Object.values(FormValueType()),
                            label: t('form.value-type'),
                            change: { identifier: "Type" }
                        },
                        {
                            id: "value",
                            clickable: false,
                            align: "left",
                            label: t('common.value'),
                            type: "textField",
                            change: { identifier: "Value" }
                        },
                        {
                            id: "color",
                            clickable: false,
                            align: "left",
                            label: t('common.color'),
                            type: "dropDown",
                            typeData: ["Red", "Green", "Blue", "Yellow", "Purple", "Orange"],
                            change: { identifier: "Color" }
                        }
                    ],
                    createCells: (headers) => {
                        let cells = [];

                        headers.forEach(header => {
                            let cell = {};

                            cell.id = header.id;

                            if (header.hasOwnProperty("change")) {
                                cell.onChange = (data, row) => {

                                    let updatedQuestion = Object.assign({}, question);

                                    const index = updatedQuestion.Options.findIndex(option => option.id === row.id);

                                    updatedQuestion.Options[index][header.change.identifier] = data

                                    setQuestion(updatedQuestion)
                                    setShouldRender(true)
                                }
                            }

                            cell.align = header.align;
                            cell.clickable = header.clickable;
                            cell.type = header.type;
                            cell.typeData = header.typeData

                            cells.push(cell);
                        });

                        return cells;
                    },
                    handleSelection: (newSelection) => {
                        setSelection(newSelection)
                    },
                    width: 12
                },
            ],
            [types.TEXT.key]: [
                {
                    type: "dropdown",
                    label: t('common.input'),
                    fieldName: "InputType",
                    data: Object.values(InputType()),
                    value: question.InputType,
                    handleChange: (value) => handleQuestionUpdate("InputType", value),
                    width: 4
                },
                {
                    type: "textField",
                    label: t('form.prefix'),
                    fieldName: "Prefix",
                    data: null,
                    value: question.Prefix,
                    handleChange: (event) => handleQuestionUpdate("Prefix", event.target.value),
                    width: 4
                },
                {
                    type: "textField",
                    label: t('form.suffix'),
                    fieldName: "Suffix",
                    data: null,
                    value: question.Suffix,
                    handleChange: (event) => handleQuestionUpdate("Suffix", event.target.value),
                    width: 4
                }
            ]
        }

        function createChoiceData(items, source) {
            if (items) {
                let data = [];

                items.forEach(item => {
                    let itemValue = item

                    if (itemValue !== undefined) {
                        data.push({ id: itemValue.id, name: itemValue.Name, type: itemValue.Type, value: itemValue.Value, color: itemValue.Color });
                    }
                });

                return data;
            }

            return []
        }
        return (
            <Box>
                <Grid container spacing={2}>
                    {
                        question.Type == null || configGridData[question.Type] == null ?
                            null :
                            configGridData[question.Type].map(element => {
                                return (
                                    <Grid item xs={element.width}>
                                        {renderComponent(element)}
                                    </Grid>
                                )
                            })
                    }
                </Grid>
            </Box>
        )
    }

    function renderContent() {
        return (
            <Box>
                <ScrollableTabs
                    isSticky
                    elevation={0}
                    tabs={Object.values(tabMap).filter(item => item.shouldRender).map(item => (
                        {
                            name: item.id,
                            content: (
                                item.render()
                            )
                        }
                    ))}
                />
            </Box>
        )
    }

    function wrapContent(id, name, content) {
        return (
            <ExpansionPanel defaultExpanded>
                <ExpansionPanelSummary
                    expandIcon={<ExpandMoreIcon />}
                >
                    <Grid container>
                        <Grid item xs={10}>

                            <TextField
                                aria-label="FormGroupTitleTextField"
                                value={question.Name}
                                label={t('form.question-name')}
                                onChange={(event) => {
                                    event.stopPropagation()
                                    handleQuestionUpdate("Name", event.target.value)
                                }}
                                onFocus={(event) => event.stopPropagation()}
                                onClick={(event) => event.stopPropagation()}
                                margin="dense"
                                variant="outlined"
                                fullWidth
                                disabled={props.disabled}
                            />
                            {/* {name != null ? name : "-"} */}

                        </Grid>

                        <Grid item xs={2}>
                            <Button
                                className={classes.deleteButton}
                                aria-label="Delete"
                                variant="outlined"
                                margin="dense"
                                onClick={props.handleDelete}
                                color="primary"
                                onFocus={(event) => event.stopPropagation()}
                            >
                                {t('common.delete')}
                            </Button>
                        </Grid>

                    </Grid>
                </ExpansionPanelSummary>
                <ExpansionPanelDetails className={classes.expansionDetails}>
                    {content}
                </ExpansionPanelDetails>
            </ExpansionPanel>
        )
    }

    return (
        wrapContent(question.id, question.Name,
            <Box className={classes.root} id={question.id}>
                <Card className={classes.questionRoot} elevation={0}>
                    {/* <CardHeader
                    classes={{
                        root: classes.cardHeader,
                        action: classes.cardHeaderAction
                    }}
                    title={
                        <EditableHeadingV2
                            value={question.Name}
                            onChange={() => { }}
                        />
                    }
                    subheader={question.subtitle}
                /> */}
                    <CardContent
                        className={classes.cardContent}
                    >
                        {renderContent()}

                    </CardContent>
                </Card>
            </Box>
        )
    )
}

FormQuestionCard.propTypes = {
    question: PropTypes.shape({
        Name: PropTypes.string,
        id: PropTypes.string,
        Tags: PropTypes.arrayOf(PropTypes.string),
        Type: PropTypes.oneOf(Object.keys(QuestionType())),
        Name: PropTypes.string,
        Hint: PropTypes.string,
        Description: PropTypes.string,
        Validation: PropTypes.arrayOf(PropTypes.string),
        IsMandatory: PropTypes.oneOf(Object.keys(MandatoryType())),
        InputType: PropTypes.oneOf(Object.keys(InputType())),
        Suffix: PropTypes.string,
        Prefix: PropTypes.string,
        Mask: PropTypes.string,
        ReferenceDataId: PropTypes.string,
        // ConfigurationOptions: {}, // Ignore for now
        // Choice
        Options: PropTypes.arrayOf(PropTypes.oneOf(Object.keys(Option))),
        MinChoices: PropTypes.number,
        MaxChoices: PropTypes.number,
        IsSticky: PropTypes.bool
    }),
    setQuestion: PropTypes.func.isRequired
}

function areEqual(prevProps, nextProps) {
    const value = JSON.stringify(prevProps) === JSON.stringify(nextProps)
    return value
}

export default React.memo(FormQuestionCard, areEqual)

